import React, { useState, useEffect } from 'react';
import { MessageCircle, Smartphone, Heart, Brain, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/resources`);
      setResources(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Failed to load resources. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(resources.map(r => r.category))];

  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(r => r.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchResources}
            className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const categoryIcons = {
    'Communication': MessageCircle,
    'Digital Safety': Smartphone,
    'Discipline': Heart,
    'Emotional Intelligence': Brain
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              Parenting Resources
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Expert guidance on navigating the challenges of fatherhood with confidence and compassion.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                selectedCategory === cat
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-700 hover:bg-amber-50 border-2 border-gray-200 hover:border-amber-300'
              }`}
            >
              {cat === 'all' ? 'All Topics' : cat}
            </button>
          ))}
        </div>

        {/* Resources List */}
        <div className="space-y-8">
          {filteredResources.map((resource) => {
            const IconComponent = categoryIcons[resource.category];
            
            return (
              <Card key={resource.id} className="border-2 hover:border-amber-300 transition-all duration-300 hover:shadow-xl">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      {IconComponent && <IconComponent className="h-7 w-7 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-semibold text-amber-600 uppercase tracking-wide">
                          {resource.category}
                        </span>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">{resource.title}</h2>
                      <p className="text-gray-600">{resource.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Topics Covered */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Topics Covered:</h3>
                    <div className="flex flex-wrap gap-2">
                      {resource.topics.map((topic, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Key Points Accordion */}
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="keypoints" className="border-2 border-gray-200 rounded-lg px-4">
                      <AccordionTrigger className="text-left font-semibold hover:text-amber-600">
                        Key Strategies & Insights
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-4 pt-4">
                          {resource.keyPoints.map((item, index) => {
                            const point = typeof item === 'string' ? item : item.point;
                            const explanation = typeof item === 'object' ? item.explanation : null;
                            return (
                              <li key={index} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                <div className="flex items-start gap-3">
                                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 flex-shrink-0 mt-2" />
                                  <div>
                                    <span className="text-gray-900 font-medium">{point}</span>
                                    {explanation && (
                                      <p className="text-gray-600 text-sm mt-2 leading-relaxed">{explanation}</p>
                                    )}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <Card className="mt-12 bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Need Personalized Guidance?</h3>
            <p className="mb-6 text-white/90 max-w-2xl mx-auto">
              Join our coaching program to get one-on-one support from Andrew Garcia, who has over 20 years of experience working with youth and families.
            </p>
            <a href="/coaching">
              <button className="px-8 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg">
                Learn About Coaching
              </button>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Resources;